import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Box, Button, Center, Flex, HStack, IconButton, Popover, PopoverBody, PopoverContent, PopoverHeader, PopoverTrigger, Text, VStack, useDisclosure,
} from '@chakra-ui/react'
import { AlertTriangle, Bell, CheckCircle2, Info, XCircle } from 'lucide-react'
import { control, type InboxItem } from '@services/operators'
import { timeAgo } from '@utils/format'

export const SEVERITY_STYLE: Record<InboxItem['severity'], { icon: React.ElementType; bg: string; fg: string }> = {
  info: { icon: Info, bg: 'blue.50', fg: 'blue.600' },
  success: { icon: CheckCircle2, bg: 'green.50', fg: 'green.600' },
  warning: { icon: AlertTriangle, bg: 'orange.50', fg: 'orange.600' },
  critical: { icon: XCircle, bg: 'red.50', fg: 'red.600' },
}

export function InboxRow({ item, onOpen, compact = false }: { item: InboxItem; onOpen: (i: InboxItem) => void; compact?: boolean }) {
  const s = SEVERITY_STYLE[item.severity] ?? SEVERITY_STYLE.info
  const Icon = s.icon
  return (
    <Flex
      as="button" type="button" w="full" textAlign="left" gap={3} px={compact ? 4 : 5} py={3} align="flex-start" onClick={() => onOpen(item)}
      bg={item.read ? 'white' : 'brand.50'} _hover={{ bg: item.read ? 'gray.50' : 'brand.100' }} transition="background .12s"
    >
      <Center w={8} h={8} borderRadius="lg" bg={s.bg} color={s.fg} flexShrink={0} mt={0.5}><Icon size={16} /></Center>
      <Box flex="1" minW={0}>
        <Text fontSize="sm" fontWeight={item.read ? 'medium' : 'semibold'} color="gray.900" noOfLines={compact ? 1 : 2}>{item.title}</Text>
        {item.body && <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={compact ? 2 : 4}>{item.body}</Text>}
        <Text fontSize="xs" color="gray.400" mt={1}>{timeAgo(item.createdAt)}</Text>
      </Box>
      {!item.read && <Box w={2} h={2} borderRadius="full" bg="brand.500" flexShrink={0} mt={2} aria-label="Unread" />}
    </Flex>
  )
}

/** Opens a notification: marks it read, then goes where it points. */
export function useOpenNotification() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const mark = useMutation((id: string) => control.markRead({ ids: [id] }), { onSuccess: () => { qc.invalidateQueries('inbox-unread'); qc.invalidateQueries('inbox') } })
  return (item: InboxItem) => {
    if (!item.read) mark.mutate(item.id)
    if (item.href) navigate(item.href)
  }
}

export default function NotificationBell() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const open = useOpenNotification()
  const unread = useQuery('inbox-unread', () => control.unread().then(r => r.data.unread), { refetchInterval: 30_000, retry: false })
  const latest = useQuery(['inbox', 'bell'], () => control.inbox({ limit: 8 }).then(r => r.data), { enabled: isOpen, retry: false })
  const readAll = useMutation(() => control.markRead({ all: true }), { onSuccess: () => { qc.invalidateQueries('inbox-unread'); qc.invalidateQueries('inbox') } })
  const count = unread.data ?? 0

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Box position="relative">
          <IconButton aria-label={count ? `${count} unread notifications` : 'Notifications'} icon={<Bell size={20} />} variant="ghost" color="gray.500" _hover={{ bg: 'gray.100', color: 'gray.600' }} borderRadius="xl" />
          {count > 0 && (
            <Center position="absolute" top="0" right="0" minW="18px" h="18px" px="1" borderRadius="full" bg="red.500" color="white" fontSize="10px" fontWeight="bold" pointerEvents="none">
              {count > 99 ? '99+' : count}
            </Center>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent w={{ base: '92vw', sm: '400px' }} borderRadius="2xl" overflow="hidden" shadow="xl">
        <PopoverHeader borderBottom="1px solid" borderColor="gray.100" px={4} py={3}>
          <Flex justify="space-between" align="center">
            <Text fontWeight="semibold">Notifications</Text>
            <Button size="xs" variant="ghost" isDisabled={!count} isLoading={readAll.isLoading} onClick={() => readAll.mutate()}>Mark all read</Button>
          </Flex>
        </PopoverHeader>
        <PopoverBody p={0} maxH="420px" overflowY="auto">
          {latest.isLoading ? <Box p={6}><Text color="gray.400" fontSize="sm">Loading…</Text></Box>
            : !latest.data?.items.length ? (
              <VStack py={10} spacing={2} color="gray.400"><Bell size={24} /><Text fontSize="sm">Nothing yet</Text></VStack>
            ) : (
              <VStack align="stretch" spacing={0} divider={<Box h="1px" bg="gray.100" />}>
                {latest.data.items.map(i => <InboxRow key={i.id} item={i} compact onOpen={x => { onClose(); open(x) }} />)}
              </VStack>
            )}
        </PopoverBody>
        <HStack borderTop="1px solid" borderColor="gray.100" p={2} justify="center">
          <Button size="sm" variant="ghost" w="full" onClick={() => { onClose(); navigate('/inbox') }}>See everything</Button>
        </HStack>
      </PopoverContent>
    </Popover>
  )
}
